import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

export const card = 'overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm';
export const th = 'px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500';
export const td = 'px-5 py-3 text-sm';
export const selectCls = 'rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm';

export const fmtDate = (raw?: string | null) =>
  raw
    ? new Date(raw).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** Header + frame shared by the Billing & Payments finance pages. */
export const FinancePage = ({
  icon: Icon,
  title,
  description,
  actions,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}) => {
  useBreadcrumbs([{ label: 'Billing & Payments', href: '/admin/payments/overview' }, { label: title }]);
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="mb-1 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <Icon className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">{title}</h1>
            </div>
            <p className="max-w-3xl text-sm text-gray-500">{description}</p>
          </div>
          {actions}
        </div>
        {children}
      </div>
    </div>
  );
};

export const ErrorNote = ({ error }: { error: unknown }) =>
  error ? (
    <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
      {(error as Error).message || 'Something went wrong.'}
    </div>
  ) : null;

export const EmptyRow = ({ cols, children }: { cols: number; children: ReactNode }) => (
  <tr>
    <td colSpan={cols} className="py-12 text-center text-sm text-gray-500">
      {children}
    </td>
  </tr>
);
